import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faSearch, faUser, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Assistance, AssistanceFormData } from '../../types/Assistance';
import { Beneficiary } from '../../types/Beneficiary';
import { getBeneficiary, getBeneficiaries, searchBeneficiaries } from '../../services/beneficiaryService';
import { getAssistance, createAssistance, updateAssistance } from '../../services/assistanceService';
import './AssistanceForm.css';

const AssistanceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId: string | null = queryParams.get('beneficiaryId');
  const isEditMode: boolean = !!id;
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [formData, setFormData] = useState<AssistanceFormData>({
    beneficiary: beneficiaryId ? parseInt(beneficiaryId) : 0,
    author: localStorage.getItem('userName') || 'Usuario',
    is_first_assistance: false,
    address: null,
    address_reference: null,
    geolocation: null,
    assistance_type: null,
    extended_assistance: false,
    extended_assistance_details: null,
    notes: null
  });
  
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [searchBeneficiaryTerm, setSearchBeneficiaryTerm] = useState('');
  const [showBeneficiarySearch, setShowBeneficiarySearch] = useState(false);
  
  // Cargar datos existentes si es edición o precarga desde un beneficiario
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        if (isEditMode && id) {
          // Modo edición - cargar datos de asistencia existente
          const assistanceData = await getAssistance(parseInt(id));
          
          // Extraer campos relevantes para el formulario
          const {
            id: _id, // No incluimos en formData
            created_at: _created, // No incluimos en formData
            updated_at: _updated, // No incluimos en formData
            ...formFields
          } = assistanceData;
          
          // Preparar datos del formulario
          setFormData({
            ...formFields,
            // Asegurar que beneficiary sea un número
            beneficiary: typeof formFields.beneficiary === 'object' && formFields.beneficiary !== null
              ? (formFields.beneficiary as any).id 
              : formFields.beneficiary
          });
          
          // Obtener el nombre del beneficiario
          try {
            // Si la asistencia tiene beneficiary_details, usar esos datos
            if ((assistanceData as any).beneficiary_details) {
              const { first_name, last_name, nickname } = (assistanceData as any).beneficiary_details;
              const name = `${first_name || ''} ${last_name || ''}`.trim();
              setBeneficiaryName(name || (nickname ? `'${nickname}'` : 'Sin nombre'));
            } 
            // Si el beneficiario es un objeto completo
            else if (typeof assistanceData.beneficiary === 'object' && assistanceData.beneficiary) {
              const beneficiary = assistanceData.beneficiary as any;
              const name = `${beneficiary.first_name || ''} ${beneficiary.last_name || ''}`.trim();
              setBeneficiaryName(name || (beneficiary.nickname ? `'${beneficiary.nickname}'` : 'Sin nombre'));
            } 
            // Si solo tenemos el ID, cargar el beneficiario
            else if (typeof assistanceData.beneficiary === 'number') {
              const beneficiaryData = await getBeneficiary(assistanceData.beneficiary);
              const name = `${beneficiaryData.first_name || ''} ${beneficiaryData.last_name || ''}`.trim();
              setBeneficiaryName(name || (beneficiaryData.nickname ? `'${beneficiaryData.nickname}'` : 'Sin nombre'));
            }
          } catch (err) {
            console.error('Error loading beneficiary details:', err);
            setBeneficiaryName(`ID: ${formData.beneficiary}`);
          }
        } else if (beneficiaryId) {
          // Si hay un beneficiaryId en la URL, cargar sus datos
          try {
            const beneficiaryData = await getBeneficiary(parseInt(beneficiaryId));
            const name = `${beneficiaryData.first_name || ''} ${beneficiaryData.last_name || ''}`.trim();
            setBeneficiaryName(name || (beneficiaryData.nickname ? `'${beneficiaryData.nickname}'` : 'Sin nombre'));
            
            // Actualizar el formData con el ID del beneficiario
            setFormData(prev => ({
              ...prev,
              beneficiary: parseInt(beneficiaryId)
            }));
          } catch (err) {
            console.error('Error loading beneficiary details:', err);
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('No se pudo cargar la información. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Segundo useEffect que maneja las dependencias
  useEffect(() => {
    // Lógica que depende de id, beneficiaryId o isEditMode
    // Se puede agregar aquí si es necesario
  }, [id, beneficiaryId, isEditMode]);
  
  // Cargar lista de beneficiarios
  useEffect(() => {
    const loadBeneficiaries = async () => {
      try {
        const data = await getBeneficiaries();
        setBeneficiaries(data);
      } catch (err) {
        console.error('Error loading beneficiaries:', err);
      }
    };

    if (!beneficiaryId && !isEditMode) {
      loadBeneficiaries();
    }
  }, [beneficiaryId, isEditMode]);
  
  // Manejar cambios en los inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Manejar inputs de tipo checkbox
    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: isChecked
      }));
      return;
    }
    
    // Manejar el resto de los inputs
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value // Convertir valores vacíos a null
    }));
  };
  
  // Buscar beneficiarios
  const handleSearchBeneficiary = async () => {
    if (!searchBeneficiaryTerm.trim()) return;
    
    try {
      const results = await searchBeneficiaries(searchBeneficiaryTerm);
      setBeneficiaries(results);
    } catch (err) {
      console.error('Error searching beneficiaries:', err);
    }
  };

  // Seleccionar un beneficiario
  const selectBeneficiary = (beneficiary: Beneficiary) => {
    setFormData(prev => ({
      ...prev,
      beneficiary: beneficiary.id || 0
    }));
    
    setBeneficiaryName(`${beneficiary.first_name || ''} ${beneficiary.last_name || ''}`.trim() || 
                      (beneficiary.nickname ? `'${beneficiary.nickname}'` : 'Sin nombre'));
    
    setShowBeneficiarySearch(false);
  };
  
  // Manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar que haya un beneficiario seleccionado
    if (!formData.beneficiary) {
      setError('Debe seleccionar un beneficiario para registrar la asistencia.');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Si estamos en modo edición, actualizar; de lo contrario, crear
      if (isEditMode && id) {
        await updateAssistance(parseInt(id), formData);
      } else {
        await createAssistance(formData);
      }
      
      // Redireccionar a la lista de asistencias
      navigate('/assistances');
    } catch (err: any) {
      console.error('Error saving assistance:', err);
      
      if (err.response && err.response.data) {
        // Mostrar errores específicos del backend si están disponibles
        const backendErrors = Object.entries(err.response.data)
          .map(([field, messages]) => `${field}: ${messages}`)
          .join(', ');
        
        setError(`Error al guardar: ${backendErrors}`);
      } else {
        setError('No se pudo guardar la información. Por favor, intente nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading && isEditMode) {
    return <div className="loading-container">Cargando información de la asistencia...</div>;
  }
  
  return (
    <div className="assistance-form-container">
      <div className="flex justify-between align-center mb-4">
        <div className="flex align-center">
          <button 
            className="btn btn-secondary mr-3"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Volver
          </button>
          <h1>{isEditMode ? 'Editar Asistencia' : 'Nueva Asistencia'}</h1>
        </div>
      </div>
      
      {error && <div className="alert alert-danger mb-4">{error}</div>}
      
      <form onSubmit={handleSubmit} className="card">
        <div className="form-section">
          <h2>Información del Beneficiario</h2>
          <div className="form-row">
            <div className="form-group full-width">
              <label className="form-label">Beneficiario</label>
              {!isEditMode ? (
                <div className="beneficiary-selection">
                  <button 
                    type="button" 
                    className="btn btn-secondary mb-3"
                    onClick={() => setShowBeneficiarySearch(!showBeneficiarySearch)}
                  >
                    {showBeneficiarySearch ? 'Ocultar búsqueda' : 'Buscar beneficiario'}
                  </button>
                  
                  {beneficiaryName && (
                    <div className="selected-beneficiary-card">
                      <div className="selected-beneficiary-header">
                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                        <span>Beneficiario seleccionado:</span>
                      </div>
                      <div className="selected-beneficiary-name">{beneficiaryName}</div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, beneficiary: 0 }));
                          setBeneficiaryName('');
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} /> Quitar
                      </button>
                    </div>
                  )}
                  
                  {showBeneficiarySearch && (
                    <div className="beneficiary-search-container">
                      <div className="form-row">
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar por nombre, apellido o DNI"
                            value={searchBeneficiaryTerm}
                            onChange={(e) => setSearchBeneficiaryTerm(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearchBeneficiary}
                          >
                            <FontAwesomeIcon icon={faSearch} /> Buscar
                          </button>
                        </div>
                      </div>
                      
                      <div className="beneficiary-list">
                        {beneficiaries.length === 0 ? (
                          <p>No se encontraron beneficiarios.</p>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>Nombre</th>
                                  <th>DNI</th>
                                  <th>Acción</th>
                                </tr>
                              </thead>
                              <tbody>
                                {beneficiaries.map((beneficiary) => (
                                  <tr key={beneficiary.id}>
                                    <td>
                                      {`${beneficiary.first_name || ''} ${beneficiary.last_name || ''}`.trim() || 
                                      (beneficiary.nickname ? `'${beneficiary.nickname}'` : 'Sin nombre')}
                                    </td>
                                    <td>{beneficiary.dni || 'No registrado'}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => selectBeneficiary(beneficiary)}
                                      >
                                        Seleccionar
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="beneficiary-info">
                  <div className="selected-beneficiary-card view-mode">
                    <div className="selected-beneficiary-header">
                      <FontAwesomeIcon icon={faUser} className="mr-2" />
                      <span>Beneficiario:</span>
                    </div>
                    <div className="selected-beneficiary-name">{beneficiaryName || `ID: ${formData.beneficiary}`}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="is_first_assistance"
                  name="is_first_assistance"
                  checked={formData.is_first_assistance}
                  onChange={handleChange}
                />
                <label htmlFor="is_first_assistance">Es la primera asistencia para este beneficiario</label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Datos del asistente</h2>
          <div className="form-row">
            <div className="form-group full-width">
              <label className="form-label" htmlFor="author">Nombre del asistente</label>
              <input
                type="text"
                id="author"
                name="author"
                className="form-control"
                value={formData.author || ''}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Detalles de la Ubicación</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="address">Dirección</label>
              <input
                type="text"
                id="address"
                name="address"
                className="form-control"
                value={formData.address || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="geolocation">Geolocalización</label>
              <input
                type="text"
                id="geolocation"
                name="geolocation"
                className="form-control"
                value={formData.geolocation || ''}
                onChange={handleChange}
                placeholder="Ejemplo: -34.603722, -58.381592"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width">
              <label className="form-label" htmlFor="address_reference">Referencia de dirección</label>
              <textarea
                id="address_reference"
                name="address_reference"
                className="form-control"
                value={formData.address_reference || ''}
                onChange={handleChange}
                rows={2}
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Detalles de la Asistencia</h2>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="assistance_type">Tipo de Asistencia</label>
              <select
                id="assistance_type"
                name="assistance_type"
                className="form-control"
                value={formData.assistance_type || ''}
                onChange={handleChange}
              >
                <option value="">Seleccione un tipo</option>
                <option value="Alimentos">Alimentos</option>
                <option value="Vestimenta">Vestimenta</option>
                <option value="Medicamentos">Medicamentos</option>
                <option value="Vivienda">Vivienda</option>
                <option value="Educación">Educación</option>
                <option value="Salud">Salud</option>
                <option value="Asistencia Legal">Asistencia Legal</option>
                <option value="Trámites">Trámites</option>
                <option value="Otros">Otros</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width">
              <label className="form-label" htmlFor="notes">Notas y observaciones</label>
              <textarea
                id="notes"
                name="notes"
                className="form-control"
                value={formData.notes || ''}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="extended_assistance"
                  name="extended_assistance"
                  className="form-check-input"
                  checked={formData.extended_assistance}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="extended_assistance">
                  Requiere seguimiento
                </label>
              </div>
            </div>
          </div>
          
          {formData.extended_assistance && (
            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label" htmlFor="extended_assistance_details">Detalles del seguimiento</label>
                <textarea
                  id="extended_assistance_details"
                  name="extended_assistance_details"
                  className="form-control"
                  value={formData.extended_assistance_details || ''}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Guardando...' : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-1" /> 
                {isEditMode ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssistanceForm;